import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Avatar,
  Box,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import {
  VideoCall,
  AccessTime,
  Person,
  LocalHospital,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
} from '@material-ui/icons';
import { useStateAuthValue } from '../../context/Auth/AuthState';
import { useStateProfConsultFilterValue } from '../../context/profConsultFilterState';
import { FilterType } from '../../utils/FilterUtil';
import professionalFoundSound from '../../assets/notifications/new-patient.wav';
import { CheckPermission } from '../../utils/CheckDesktopPermissions';
import Modal from '../../components/Modal';
import { ApplicationStyle, Colors } from '../../theme';
import firebase from '../../services/firebase';
import { useStateProfessionalDashboardValue } from '../../context/profesionalPage/profesionalDashboardState';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 20,
    minWidth: 1000,
    maxWidth: 1200,
    boxShadow: '0 8px 32px rgba(0, 44, 84, 0.2)',
    border: `2px solid ${Colors.AzulMediline}`,
  },
  dialogContent: {
    textAlign: 'center',
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  },
  previewContainer: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(3),
    alignItems: 'flex-start',
  },
  videoPreview: {
    flex: 2,
    position: 'relative',
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    overflow: 'hidden',
    aspectRatio: '16/9',
    minHeight: 400,
    maxHeight: 500,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
    border: `3px solid ${Colors.AzulMediline}`,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)',
  },
  videoPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
    color: 'white',
    fontSize: '18px',
    fontWeight: 500,
  },
  mediaControls: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    right: 16,
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
  controlButton: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: 'white',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  micButton: {
    backgroundColor: (props) => props.isMicMuted ? '#f44336' : '#4caf50',
  },
  cameraButton: {
    backgroundColor: (props) => props.isCameraOff ? '#f44336' : '#4caf50',
  },
  audioIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '20px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
  },
  audioLevel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  audioBar: {
    width: '3px',
    backgroundColor: (props) => props.audioLevel > 10 ? '#4caf50' : '#757575',
    borderRadius: '2px',
    transition: 'all 0.1s ease',
  },
  consultInfo: {
    flex: 1,
    textAlign: 'left',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  avatar: {
    width: 120,
    height: 120,
    margin: '0 auto 24px',
    backgroundColor: Colors.AzulMediline,
    fontSize: '3.5rem',
    boxShadow: '0 4px 20px rgba(0, 44, 84, 0.3)',
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
    color: Colors.AzulMediline,
    fontSize: '28px',
    fontFamily: 'Raleway',
  },
  subtitle: {
    marginBottom: theme.spacing(4),
    color: Colors.GrisMedio,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '1.4',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: `1px solid ${Colors.AzulMediline}20`,
    boxShadow: '0 2px 8px rgba(0, 44, 84, 0.08)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0, 44, 84, 0.12)',
    },
  },
  infoIcon: {
    marginRight: theme.spacing(2),
    color: Colors.AzulMediline,
    fontSize: '24px',
  },
  infoText: {
    flexGrow: 1,
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: 500,
    color: Colors.Negro,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  acceptButton: {
    ...ApplicationStyle.btnBlueNext,
    width: '100%',
    padding: theme.spacing(2, 4),
    fontSize: '18px',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 44, 84, 0.3)',
    '&:hover': {
      backgroundColor: '#003F77',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 44, 84, 0.4)',
    },
  },
  declineButton: {
    ...ApplicationStyle.btnRedNextOutline,
    width: '100%',
    padding: theme.spacing(1.5, 4),
    fontSize: '16px',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: Colors.RojoMediline,
      color: 'white',
      transform: 'translateY(-2px)',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(4),
  },
  loadingSpinner: {
    color: Colors.AzulMediline,
  },
  loadingText: {
    color: Colors.AzulMediline,
    fontSize: '20px',
    fontWeight: 600,
    fontFamily: 'Raleway',
  },
}));

const MedilineStyle = makeStyles(ApplicationStyle);

const getSpecialtyName = (specialtyId) => {
  return 'Veterinaria';
};

export default function QueueJoinModal({ open, queueData, stompClient, onAccept, onDecline, patient=true, handleDeclineProf=null }) {
  const styleProps = { isMicMuted: false, isCameraOff: false, audioLevel: 0 };
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasAudioDevice, setHasAudioDevice] = useState(false);
  const [hasVideoDevice, setHasVideoDevice] = useState(false);

  styleProps.isMicMuted = isMicMuted;
  styleProps.isCameraOff = isCameraOff;
  styleProps.audioLevel = audioLevel;

  const classes = useStyles(styleProps);
  const MedilineClasses = MedilineStyle();
  const [loading, setLoading] = useState(false);
  const [{ userData }] = useStateAuthValue();
  const [, dispatch] = patient ? useStateProfConsultFilterValue() : useStateProfessionalDashboardValue();
  const [hasPermission, setHasPermission] = useState(false);
  const [askPermission, setAskPermission] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const isMountedRef = useRef(true);
  const [timeRemaining, setTimeRemaining] = useState(300);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanupStream();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!patient && open) {
      setTimeRemaining(300);
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [patient, open]);

  useEffect(() => {
    if (hasPermission && open) {
      initializeMediaStream();
    }
  }, [hasPermission, open]);


  const initializeMediaStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      setLocalStream(stream);

      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();

      setHasAudioDevice(audioTracks.length > 0);
      setHasVideoDevice(videoTracks.length > 0);

      setIsMicMuted(!audioTracks.length || !audioTracks[0].enabled);
      setIsCameraOff(!videoTracks.length || !videoTracks[0].enabled);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      if (audioTracks.length > 0) {
        setupAudioLevelMonitoring(stream);
      }
    } catch (err) {
      console.error('Error accessing media devices:', err);
      setHasAudioDevice(false);
      setHasVideoDevice(false);
      setIsMicMuted(true);
      setIsCameraOff(true);
    }
  };

  const setupAudioLevelMonitoring = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const monitorAudioLevel = () => {
        if (!analyserRef.current || !isMountedRef.current) return;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);

        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average);

        if (isMountedRef.current) {
          requestAnimationFrame(monitorAudioLevel);
        }
      };

      monitorAudioLevel();
    } catch (err) {
      console.error('Error setting up audio monitoring:', err);
    }
  };

  const cleanupStream = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
  };



  const handleDecline = () => {
    cleanupStream();

    const wsMessage = {
      patientId: queueData.patientid,
      orderNumber: queueData.order_number,
      specialtyId: queueData.specialty_id,
      command: "PatientCancel",
      professionalId: queueData.professionalid,
      queueId: queueData.queue_id,
      reason: queueData.reason,
    };
    
    firebase.put({...wsMessage, status: 'QUIT'});
    
    if (stompClient && stompClient.connected) {
      stompClient.send("/app/message", {}, JSON.stringify(wsMessage));
    }
    
    localStorage.setItem("consult", null);
    
    dispatch({
      type: "LOADING",
      clickButton: FilterType.FINISH_CONSULTATION,
      loading: false,
    });

    window.history.replaceState({}, document.title, window.location.pathname);
    onDecline();
  };

  const handleAccept = async () => {
    if (!hasPermission) {
      setAskPermission(true);
      return;
    }
    
    if (!queueData || !stompClient) return;
    
    setLoading(true);

    const payloadData = {
      patientId: queueData.patientid,
      orderNumber: queueData.order_number,
      professionalId: queueData.professionalid,
      queueId: queueData.queue_id,
      profesionalName: `${queueData.professional_first_name} ${queueData.professional_last_name}`,
      specialtyId: queueData.specialty_id,
      reason: queueData.reason,
    };

    const patient = {
      ...userData,
      patientId: payloadData.patientId,
      orderNumber: payloadData.orderNumber,
      professionalId: payloadData.professionalId,
      queueId: payloadData.queueId,
      profesionalName: payloadData.profesionalName,
      specialtyId: payloadData.specialtyId,
      reason: payloadData.reason,
      connected: userData.connected,
      profImage: payloadData.profImage,
      profSpecialty: payloadData.profSpecialty,
    };

    const virtualGuardData = {
      ...payloadData,
      uid: payloadData.patientId,
      role: "subscriber",
      channel: payloadData.queueId,
      layout: 1,
    };

    dispatch({
      type: "VIRTUAL_GUARD",
      stomp: stompClient,
      data: payloadData,
    });

    const audio = new Audio(professionalFoundSound);
    audio.addEventListener("canplaythrough", () => {
      audio.play();
    });

    const wsMessage = {
      patientId: payloadData.patientId,
      orderNumber: payloadData.orderNumber,
      specialtyId: payloadData.specialtyId,
      command: "Accept",
      professionalId: payloadData.professionalId,
      queueId: payloadData.queueId,
      reason: payloadData.reason,
    };

    if (stompClient && stompClient.connected) {
      stompClient.send("/app/message", {}, JSON.stringify(wsMessage));
    }

    setTimeout(() => {
      dispatch({
        type: "LOADING",
        clickButton: FilterType.START_VIRTUAL_GUARD_CONNECTION,
        loading: false,
      });
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      onAccept(virtualGuardData);
    }, 3000);
  };

  if (!queueData) return null;


  if (loading) {
    return (
      <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.loadingContainer}>
            <CircularProgress size={60} className={classes.loadingSpinner} />
            <Typography variant="h6" className={classes.loadingText}>
              🚀 Conectando con el profesional...
            </Typography>
            <Typography variant="body2" style={{ color: Colors.GrisMedio, marginTop: '12px' }}>
              Esto tomará unos momentos
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <CheckPermission setHasPermission={setHasPermission} askPermission={askPermission} setAskPermission={setAskPermission}/>
      <Dialog open={open} classes={{ paper: classes.dialogPaper }}>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="h4" className={classes.title}>
            👩‍⚕️ Consulta Médica
          </Typography>

          {patient ? (
            <Typography variant="h6" className={classes.subtitle}>
              El Dr. <strong>{queueData.professional_first_name} {queueData.professional_last_name}</strong> está listo para atenderte
            </Typography>
          ) :           
            <Typography variant="h6" className={classes.subtitle}>
              <p>Paciente encontrado, esperando su respuesta...</p>
            </Typography>
          }
          <div className={classes.previewContainer}>
            <div className={classes.videoPreview}>
              {hasPermission && localStream ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className={classes.video}
                    style={{ display: isCameraOff ? 'none' : 'block' }}
                  />
                  {isCameraOff && (
                    <div className={classes.videoPlaceholder}>
                      <VideocamOff style={{ fontSize: '48px', marginBottom: '16px' }} />
                      <Typography>Cámara desactivada</Typography>
                    </div>
                  )}

                  <div className={classes.mediaControls}>
                    <div
                      className={`${classes.controlButton} ${classes.micButton}`}
                      style={{
                        opacity: (!hasPermission || !hasAudioDevice) ? 0.5 : 1,
                        cursor: 'default'
                      }}
                      title={
                        !hasPermission ? 'Sin permisos de micrófono' :
                        !hasAudioDevice ? 'Micrófono no detectado' :
                        (isMicMuted ? 'Micrófono desactivado' : 'Micrófono activo')
                      }
                    >
                      {(!hasPermission || !hasAudioDevice || isMicMuted) ? <MicOff /> : <Mic />}
                    </div>

                    <div
                      className={`${classes.controlButton} ${classes.cameraButton}`}
                      style={{
                        opacity: (!hasPermission || !hasVideoDevice) ? 0.5 : 1,
                        cursor: 'default'
                      }}
                      title={
                        !hasPermission ? 'Sin permisos de cámara' :
                        !hasVideoDevice ? 'Cámara no detectada' :
                        (isCameraOff ? 'Cámara desactivada' : 'Cámara activa')
                      }
                    >
                      {(!hasPermission || !hasVideoDevice || isCameraOff) ? <VideocamOff /> : <Videocam />}
                    </div>
                  </div>

                  {hasPermission && hasAudioDevice && !isMicMuted && (
                    <div className={classes.audioIndicator} title="Nivel de audio">
                      <VolumeUp style={{ fontSize: '16px' }} />
                    </div>
                  )}
                </>
              ) : (
                <div className={classes.videoPlaceholder}>
                  <LocalHospital style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Typography>
                    {!hasPermission ? 'Verificando permisos...' : 'Cargando vista previa...'}
                  </Typography>
                </div>
              )}
            </div>
            <div className={classes.consultInfo}>
              {patient ? (
                <Box className={classes.infoBox}>
                  <LocalHospital className={classes.infoIcon} />
                  <Typography className={classes.infoText}>
                    <strong>Especialidad:</strong> {getSpecialtyName(queueData.specialty_id)}
                  </Typography>
                </Box>
              ):
                <Box className={classes.infoBox}>
                  <LocalHospital className={classes.infoIcon} />
                  <Typography className={classes.infoText}>
                    <strong>Paciente:</strong> {(queueData?.first_name || '') + ' ' + (queueData?.last_name || '')}
                  </Typography>
                </Box>
              }

              <Box className={classes.infoBox}>
                <Person className={classes.infoIcon} />
                <Typography className={classes.infoText}>
                  <strong>Motivo de consulta:</strong> {queueData.reason}
                </Typography>
              </Box>

              <div className={classes.buttonContainer}>
                {patient && (
                  <Button
                    variant="contained"
                    className={classes.acceptButton}
                    onClick={handleAccept}
                    disabled={!hasPermission}
                  >
                    {!hasPermission ? '🔒 Verificando permisos...' : '🎥 Unirse a la consulta'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  className={classes.declineButton}
                  onClick={patient ? handleDecline : handleDeclineProf}
                  disabled={!patient && timeRemaining > 0}
                >
                  {!patient && timeRemaining > 0
                    ? `❌ Cancelar consulta (${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')})`
                    : '❌ Cancelar consulta'
                  }
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}